import React, { useEffect, useMemo, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { sortTablesByNumber } from '../lib/tableSort';
import { Plus, Trash2, QrCode, Download, UserCheck, Wand2, CheckSquare, Square, Pencil } from 'lucide-react';
import { Modal } from '../lib/Modal';
import { useAuth } from '../lib/AuthContext';
import { hasPlanFeature, isAtPlanLimit, planUsageLabel } from '../lib/plans';
import { PlanUpgradeNotice } from '../components/PlanUpgradeNotice';

const cornerStyle = {
    position: 'absolute',
    width: 26,
    height: 26,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    minWidth: 0,
};

export default function TablesPage() {
    const { restaurant } = useAuth();
    const [tables, setTables] = useState([]);
    const [planFeatures, setPlanFeatures] = useState(null);
    const [waiters, setWaiters] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [qrTable, setQrTable] = useState(null);
    const [qrData, setQrData] = useState(null);
    const [form, setForm] = useState({ number: '', zone: 'Sală', capacity: 4 });
    const [editTable, setEditTable] = useState(null);
    const [editForm, setEditForm] = useState({ number: '', zone: 'Sală', capacity: 4 });
    const [assignTable, setAssignTable] = useState(null);
    const [assignWaiterId, setAssignWaiterId] = useState('');
    const [filterWaiter, setFilterWaiter] = useState('all');
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkWaiterId, setBulkWaiterId] = useState('');
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        const [tablesRes, staffRes] = await Promise.all([api.get('/r/tables'), api.get('/r/staff')]);
        setTables(tablesRes.data.items);
        setPlanFeatures(tablesRes.data.plan_features || staffRes.data.plan_features || null);
        setWaiters(staffRes.data.items.filter(s => s.role === 'waiter'));
    };

    useEffect(() => { reload(); }, []);

    const stats = useMemo(() => {
        const unassigned = tables.filter(t => !t.assigned_to).length;
        const byWaiter = waiters.map(w => ({
            ...w,
            count: tables.filter(t => t.assigned_to === w.id).length,
        }));
        return { unassigned, byWaiter };
    }, [tables, waiters]);

    const visibleTables = useMemo(() => {
        let list = tables;
        if (filterWaiter === 'unassigned') list = tables.filter(t => !t.assigned_to);
        else if (filterWaiter !== 'all') list = tables.filter(t => t.assigned_to === filterWaiter);
        return sortTablesByNumber(list, { groupByZone: true });
    }, [tables, filterWaiter]);
    const zones = [...new Set(visibleTables.map(t => t.zone))];

    const create = async () => {
        try {
            await api.post('/r/tables', { ...form, capacity: parseInt(form.capacity) });
            setShowAdd(false);
            setForm({ number: '', zone: 'Sală', capacity: 4 });
            reload();
        } catch (e) { alert(formatApiError(e)); }
    };

    const openEdit = (t) => {
        setEditTable(t);
        setEditForm({ number: t.number || '', zone: t.zone || 'Sală', capacity: t.capacity || 4 });
    };

    const saveEdit = async () => {
        if (!editTable) return;
        setBusy(true);
        try {
            await api.put(`/r/tables/${editTable.id}`, {
                number: editForm.number,
                zone: editForm.zone,
                capacity: parseInt(editForm.capacity) || 1,
            });
            setEditTable(null);
            reload();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (window.confirm('Ștergi masa?')) {
            await api.delete(`/r/tables/${id}`);
            reload();
        }
    };

    const showQr = async (t) => {
        setQrTable(t);
        const { data } = await api.get(`/r/tables/${t.id}/qr`);
        setQrData(data);
    };

    const [bulkQrBusy, setBulkQrBusy] = useState(false);
    const downloadAllQrPdf = async () => {
        if (bulkQrBusy) return;
        setBulkQrBusy(true);
        try {
            const res = await api.get('/r/tables/qr-pdf', { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `qr-mese-${restaurant?.slug || 'restaurant'}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBulkQrBusy(false);
        }
    };

    const openAssign = (table, e) => {
        if (e?.target?.closest('button')) return;
        if (bulkMode) {
            toggleSelect(table.id);
            return;
        }
        setAssignTable(table);
        setAssignWaiterId(table.assigned_to || '');
    };

    const saveAssign = async () => {
        if (!assignTable) return;
        setBusy(true);
        try {
            await api.post(`/r/tables/${assignTable.id}/assign`, {
                waiter_id: assignWaiterId || null,
            });
            setAssignTable(null);
            reload();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectZone = (zone) => {
        const ids = visibleTables.filter(t => t.zone === zone).map(t => t.id);
        setSelectedIds(prev => {
            const next = new Set(prev);
            const allSelected = ids.every(id => next.has(id));
            ids.forEach(id => (allSelected ? next.delete(id) : next.add(id)));
            return next;
        });
    };

    const bulkAssign = async () => {
        if (!selectedIds.size) return alert('Selectează cel puțin o masă.');
        setBusy(true);
        try {
            await api.post('/r/tables/assign-bulk', {
                table_ids: [...selectedIds],
                waiter_id: bulkWaiterId || null,
            });
            setSelectedIds(new Set());
            setBulkMode(false);
            reload();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const autoAssign = async (onlyUnassigned) => {
        const label = onlyUnassigned
            ? 'Repartizezi doar mesele neasignate egal între ospătari?'
            : 'Reasignezi TOATE mesele egal între ospătari? Asignările actuale se pierd.';
        if (!window.confirm(label)) return;
        setBusy(true);
        try {
            const { data } = await api.post('/r/tables/assign-auto', { only_unassigned: onlyUnassigned });
            const summary = Object.entries(data.per_waiter || {})
                .map(([name, count]) => `${name}: ${count}`)
                .join('\n');
            alert(`Asignate ${data.updated} mese.\n${summary || 'Nicio masă de repartizat.'}`);
            reload();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const canBulkAssign = hasPlanFeature({ plan_features: planFeatures }, 'waiter_bulk_assign');
    const tablesAtLimit = isAtPlanLimit({ plan_features: planFeatures }, 'tables');

    if (!hasPlanFeature(restaurant, 'table_management')) {
        return (
            <div className="fade-in-up" data-testid="tables-page">
                <div className="kicker">Mese & QR</div>
                <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 1.4rem' }}>Mese & QR pe masă.</h1>
                <PlanUpgradeNotice featureLabel="Gestionarea meselor și QR per masă" />
                <p style={{ color: 'var(--text-muted)', fontSize: '0.92rem', lineHeight: 1.6, maxWidth: 560 }}>
                    Pe planul Standard ai un <strong style={{ color: 'var(--amber)' }}>singur QR de meniu</strong>, pe care îl găsești în
                    secțiunea <strong>Meniu</strong> — îl poți descărca și printa. Treci la <strong>Pro</strong> pentru mese nelimitate,
                    QR per masă, comenzi online și asignare ospătari.
                </p>
            </div>
        );
    }

    return (
        <div className="fade-in-up" data-testid="tables-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginBottom: '1.4rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <div className="kicker">Mese & QR</div>
                    <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 0' }}>Planul tău de săli.</h1>
                    {planFeatures && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 6 }}>
                            Mese: {planUsageLabel({ plan_features: planFeatures }, 'tables')}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-secondary" onClick={downloadAllQrPdf} disabled={bulkQrBusy || !tables.length} data-testid="download-all-qr-pdf"
                            title="PDF A4 cu toate QR-urile, etichetate Masa 1, Masa 2, ... — gata de printat">
                        {bulkQrBusy ? <span className="spinner" /> : <Download size={16} />}
                        {bulkQrBusy ? ' Se generează...' : ' Descarcă PDF cu QR-urile meselor'}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => setShowAdd(true)} disabled={tablesAtLimit} data-testid="add-table">
                        <Plus size={16} /> Masă nouă
                    </button>
                </div>
            </div>

            <div className="panel" style={{ marginBottom: '1.2rem' }} data-testid="assignment-panel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem', flexWrap: 'wrap' }}>
                    <div>
                        <div className="kicker">Asignări ospătari</div>
                        <p style={{ margin: '0.35rem 0 0', color: 'var(--text-muted)', fontSize: '0.9rem', maxWidth: 560 }}>
                            Repartizează mesele între ospătari. Fiecare ospătar vede doar comenzile de la mesele lui.
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy || !waiters.length || !canBulkAssign}
                                onClick={() => autoAssign(true)} data-testid="auto-assign-unassigned">
                            <Wand2 size={14} /> Repartizează neasignate
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy || !waiters.length || !canBulkAssign}
                                onClick={() => autoAssign(false)} data-testid="auto-assign-all">
                            <Wand2 size={14} /> Reasignează toate
                        </button>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: '1rem' }}>
                    <span className="badge badge-muted">{tables.length} mese</span>
                    <span className="badge badge-cyan">{stats.unassigned} neasignate</span>
                    {stats.byWaiter.map(w => (
                        <button key={w.id} type="button"
                                className={`badge ${filterWaiter === w.id ? 'badge-amber' : 'badge-teal'}`}
                                style={{ cursor: 'pointer', border: 'none' }}
                                onClick={() => setFilterWaiter(filterWaiter === w.id ? 'all' : w.id)}>
                            {w.name}: {w.count}
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginTop: '1rem', alignItems: 'end' }}>
                    <div>
                        <label className="label">Filtru</label>
                        <select className="input" value={filterWaiter} onChange={e => setFilterWaiter(e.target.value)} data-testid="filter-waiter">
                            <option value="all">Toate mesele</option>
                            <option value="unassigned">Doar neasignate</option>
                            {waiters.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                        </select>
                    </div>
                    <button type="button" className={`btn btn-sm ${bulkMode ? 'btn-primary' : 'btn-secondary'}`}
                            disabled={!canBulkAssign}
                            onClick={() => { setBulkMode(v => !v); setSelectedIds(new Set()); }} data-testid="toggle-bulk-mode">
                        {bulkMode ? <CheckSquare size={14} /> : <Square size={14} />} Selectare multiplă
                    </button>
                    {bulkMode && (
                        <>
                            <div>
                                <label className="label">Ospătar pentru selecție</label>
                                <select className="input" value={bulkWaiterId} onChange={e => setBulkWaiterId(e.target.value)} data-testid="bulk-waiter">
                                    <option value="">— Neasignat —</option>
                                    {waiters.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                            </div>
                            <button type="button" className="btn btn-primary btn-sm" disabled={busy || !selectedIds.size}
                                    onClick={bulkAssign} data-testid="bulk-assign">
                                <UserCheck size={14} /> Asignează {selectedIds.size} mese
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: '1.2rem', flexWrap: 'wrap' }}>
                <div className="badge badge-muted">Liberă</div>
                <div className="badge badge-amber">Ocupată</div>
                <div className="badge badge-cyan">Rezervată</div>
            </div>

            {zones.map(z => (
                <div key={z} style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0.6rem 0' }}>
                        <h2 className="headline" style={{ fontSize: '1.4rem', margin: 0 }}>{z}</h2>
                        {bulkMode && (
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => selectZone(z)}>
                                Selectează zona
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.9rem' }}>
                        {visibleTables.filter(t => t.zone === z).map(t => {
                            const selected = selectedIds.has(t.id);
                            return (
                                <div key={t.id}
                                     className={`table-cell table-cell-${t.status} ${selected ? 'table-cell-selected' : ''}`}
                                     data-testid={`table-${t.id}`}
                                     onClick={(e) => openAssign(t, e)}
                                     style={{ cursor: bulkMode ? 'pointer' : 'default' }}>
                                    {bulkMode ? (
                                        <div style={{ ...cornerStyle, top: 6, left: 6, color: selected ? 'var(--amber)' : 'var(--text-faint)' }}>
                                            {selected ? <CheckSquare size={14} /> : <Square size={14} />}
                                        </div>
                                    ) : (
                                        <button type="button" className="btn btn-ghost"
                                                style={{ ...cornerStyle, top: 4, left: 4 }}
                                                onClick={(e) => { e.stopPropagation(); showQr(t); }}
                                                title="QR masă"
                                                data-testid={`qr-${t.id}`}>
                                            <QrCode size={14} />
                                        </button>
                                    )}
                                    {!bulkMode && (
                                        <button type="button" className="btn btn-ghost"
                                                style={{ ...cornerStyle, top: 4, right: 4 }}
                                                onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                                                title="Editează masa"
                                                data-testid={`edit-${t.id}`}>
                                            <Pencil size={14} />
                                        </button>
                                    )}
                                    {!bulkMode && (
                                        <button type="button" className="btn btn-ghost"
                                                style={{ ...cornerStyle, bottom: 4, left: 4 }}
                                                onClick={(e) => { e.stopPropagation(); setAssignTable(t); setAssignWaiterId(t.assigned_to || ''); }}
                                                title="Asignează ospătar">
                                            <UserCheck size={14} />
                                        </button>
                                    )}
                                    <button type="button" className="btn btn-ghost"
                                            style={{ ...cornerStyle, bottom: 4, right: 4 }}
                                            onClick={(e) => { e.stopPropagation(); remove(t.id); }}
                                            title="Șterge masa">
                                        <Trash2 size={14} color="var(--neon-magenta)" />
                                    </button>
                                    <div className="headline" style={{ fontSize: '2rem', lineHeight: 1 }}>{t.number}</div>
                                    <div className="kicker" style={{ fontSize: '0.66rem', marginTop: 2 }}>{t.capacity} pers.</div>
                                    <div style={{ fontSize: '0.68rem', color: t.assigned_waiter_name ? 'var(--neon-teal)' : 'var(--text-faint)', marginTop: 4, minHeight: 14, padding: '0 28px', textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-word' }}
                                         data-testid={`table-waiter-${t.id}`}>
                                        {t.assigned_waiter_name ? `👤 ${t.assigned_waiter_name}` : 'Neasignată'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {!visibleTables.length && (
                <div className="panel" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    Nicio masă în acest filtru.
                </div>
            )}

            <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Masă nouă">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                    <div><label className="label">Număr</label><input className="input" value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} data-testid="table-number" /></div>
                    <div><label className="label">Zonă</label>
                        <select className="input" value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })} data-testid="table-zone">
                            <option>Sală</option><option>Terasă</option><option>VIP</option><option>Bar</option>
                        </select>
                    </div>
                    <div><label className="label">Capacitate</label><input className="input" type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
                    <div style={{ display: 'flex', gap: '0.6rem' }}>
                        <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowAdd(false)}>Anulează</button>
                        <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={create} disabled={!form.number} data-testid="table-create">Creează</button>
                    </div>
                </div>
            </Modal>

            <Modal open={!!editTable} onClose={() => setEditTable(null)} title={editTable ? `Editează masa ${editTable.number}` : ''} maxWidth={420}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                    <div>
                        <label className="label">Număr</label>
                        <input className="input" value={editForm.number}
                               onChange={e => setEditForm({ ...editForm, number: e.target.value })}
                               data-testid="edit-table-number" />
                    </div>
                    <div>
                        <label className="label">Zonă</label>
                        <input className="input" value={editForm.zone}
                               list="edit-zone-options"
                               onChange={e => setEditForm({ ...editForm, zone: e.target.value })}
                               data-testid="edit-table-zone" />
                        <datalist id="edit-zone-options">
                            <option value="Sală" />
                            <option value="Terasă" />
                            <option value="VIP" />
                            <option value="Bar" />
                        </datalist>
                    </div>
                    <div>
                        <label className="label">Capacitate</label>
                        <input className="input" type="number" min={1} value={editForm.capacity}
                               onChange={e => setEditForm({ ...editForm, capacity: e.target.value })} />
                    </div>
                    <div style={{ display: 'flex', gap: '0.6rem' }}>
                        <button type="button" className="btn btn-secondary" style={{ flex: 1 }}
                                onClick={() => setEditTable(null)}>Anulează</button>
                        <button type="button" className="btn btn-primary" style={{ flex: 1 }}
                                disabled={busy || !editForm.number}
                                onClick={saveEdit} data-testid="edit-table-save">Salvează</button>
                    </div>
                </div>
            </Modal>

            <Modal open={!!assignTable} onClose={() => setAssignTable(null)} title={assignTable ? `Masa ${assignTable.number}` : ''} maxWidth={420}>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginTop: 0 }}>
                    Alege ospătarul responsabil de această masă.
                </p>
                <select className="input" value={assignWaiterId} onChange={e => setAssignWaiterId(e.target.value)} data-testid="assign-waiter-select">
                    <option value="">— Neasignată —</option>
                    {waiters.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem' }}>
                    <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setAssignTable(null)}>Anulează</button>
                    <button type="button" className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={saveAssign} data-testid="assign-waiter-save">
                        Salvează asignarea
                    </button>
                </div>
            </Modal>

            <Modal open={!!qrTable && !!qrData} onClose={() => { setQrTable(null); setQrData(null); }} title={qrTable ? `QR Masa ${qrTable.number}` : ''}>
                {qrData && (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ background: '#fff', padding: 16, borderRadius: 12, display: 'inline-block' }}>
                            <img src={qrData.qr_base64} alt="QR" style={{ width: 240, height: 240, display: 'block' }} data-testid="qr-image" />
                        </div>
                        <div style={{ marginTop: '1rem', color: 'var(--text-muted)', fontSize: '0.82rem', wordBreak: 'break-all' }}>{qrData.url}</div>
                        <a href={qrData.qr_base64} download={`qr-masa-${qrTable?.number}.png`} className="btn btn-primary" style={{ marginTop: '1rem' }} data-testid="qr-download">
                            <Download size={16} /> Descarcă PNG
                        </a>
                    </div>
                )}
            </Modal>
        </div>
    );
}
